import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import { SidebarContainer, NavList } from './NavBar.styles';
import { NavItem } from './NavItem';
import { CollapseButton } from './CollapseButton';
import { getNavItems } from './NavBar.config';
import { useUser } from '@increasecard/increase-core';

export function NavBar(): JSX.Element {
  const { t } = useTranslation(['common', 'billing', 'settingsMe', 'settingsUsers']);
  const location = useLocation();
  const [expandedPath, setExpandedPath] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const user = useUser();
  const isAggregatorAccount = !user?.isLegacyAccount();

  const navItems = useMemo(() => getNavItems(t, isAggregatorAccount), [t, isAggregatorAccount]);

  useEffect(() => {
    // Auto-expand based on current URL
    const currentPath = location.pathname;
    const parent = navItems.find((item) => {
      if (!item.children) return false;

      if (currentPath.startsWith(item.path) && item.path !== '/') return true;

      return item.children.some((child) =>
        child.exact ? currentPath === child.path : currentPath.startsWith(child.path),
      );
    });

    if (parent) {
      setExpandedPath(parent.path);
    }
  }, [location.pathname, navItems]);

  const togglePath = (path: string) => {
    setExpandedPath((prev) => (prev === path ? '' : path));
  };

  return (
    <SidebarContainer $isCollapsed={isCollapsed} className="navbar">
      <CollapseButton isCollapsed={isCollapsed} onClick={() => setIsCollapsed(!isCollapsed)} />
      <NavList>
        {navItems
          .filter((item) => !item.hidden)
          .map((item) => (
            <NavItem
              key={item.path}
              item={item}
              isCollapsed={isCollapsed}
              isExpanded={expandedPath.includes(item.path)}
              onToggle={togglePath}
            />
          ))}
      </NavList>
    </SidebarContainer>
  );
}
